import * as THREE from 'three'
import CustomShaderMaterial from '../libs/three-custom-shader-material'
import { CHUNK_WIDTH } from './chunks'

// // fix fog distance calc
// // see: https://github.com/mrdoob/three.js/issues/14601
// // future: https://www.youtube.com/watch?v=k1zGz55EqfU
// THREE.ShaderChunk.fog_vertex = `
// #ifdef USE_FOG

//   // original
//   // vFogDepth = - mvPosition.z;

//   // radial distance
//   // vFogDepth = length( mvPosition );

//   // cylindrical (ignore altitude)
//   vFogDepth = length( mvPosition.xz );

//   // height-based (eg ground fog)
//   // vFogDepth = abs( mvPosition.y );

// #endif
// `

// // cylindrical fog using world position so its stable when tilting the camera up/down
// THREE.ShaderChunk.fog_vertex = `
// #ifdef USE_FOG
//   vec3 worldPos = (modelMatrix * vec4(position, 1.0)).xyz;
//   // #ifdef USE_INSTANCING
//   //   worldPos = (instanceMatrix * vec4(worldPos, 1.0)).xyz;
//   // #endif
//   vFogDepth = length( (worldPos - cameraPosition) * vec3(1.0, 0.0, 1.0) );
// #endif
// `

// cylindrical-ish fog using Chebyshev distance on XZ in world space
THREE.ShaderChunk.fog_vertex = `
#ifdef USE_FOG
  vec3 worldPos = (modelMatrix * vec4(position, 1.0)).xyz;

  // If you actually use instancing, re-enable this block and compute from object space:
  // #ifdef USE_INSTANCING
  //   worldPos = (instanceMatrix * vec4(worldPos, 1.0)).xyz;
  // #endif

  vec3 d = worldPos - cameraPosition;

  // Chebyshev distance on the ground plane (XZ). Ignores Y to make it tilt-stable.
  float chebXZ = max(abs(d.x), abs(d.z));

  // three.js just uses vFogDepth as "distance"; any scalar works.
  vFogDepth = chebXZ;
#endif
`

export function createSky() {
  const textureLoader = new THREE.TextureLoader()
  let lut
  const sampler = createSampler()
  const uniforms = {
    uElapsed: { value: 0 }, // delta increments
    uTime: { value: 0 }, // 0 midnight, 0.5 noon, 1 midnight, repeating
    uSkyLUT: { value: null },
    uSkyLUTSize: { value: new THREE.Vector2(0, 0) },
    uWindStrength: { value: 0.15 }, // how far vertices displace
    uWindDirection: { value: new THREE.Vector2(1.0, 0.3) }, // XZ direction
  }

  let material = new THREE.MeshBasicMaterial({
    depthWrite: false,
    depthTest: false,
    side: THREE.BackSide,
    toneMapped: false,
    fog: false,
  })
  material = new CustomShaderMaterial({
    baseMaterial: material,
    uniforms: {
      ...uniforms,
    },
    vertexShader: `
        varying vec3 vWorldPos;

        void main() {
          // transform to world
          vec4 wp = modelMatrix * vec4(position, 1.0);
          vWorldPos = wp.xyz;
          csm_PositionRaw = projectionMatrix * viewMatrix * wp;
        }
      `,
    fragmentShader: `
        precision highp float;

        uniform float uTime;
        uniform sampler2D uSkyLUT;
        uniform vec2 uSkyLUTSize;

        varying vec3 vWorldPos;

        vec3 sampleRow(float u, float rowIndex) {
          // sample at column u, row center
          float v = (rowIndex + 0.5) / uSkyLUTSize.y;
          return texture2D(uSkyLUT, vec2(u, v)).rgb;
        }

        void main() {
          // normalized view direction in world space
          vec3 dir = normalize(vWorldPos - cameraPosition);

          // time → column at texel centers (assumes uTime in [0,1) across a day)
          float u = (fract(uTime) * uSkyLUTSize.x + 0.5) / uSkyLUTSize.x;

          // fetch triplet (TOP / horizon / BOTTOM)
          vec3 cTop     = sampleRow(u, 0.0);  // row 0: TOP of the sky
          vec3 cHorizon = sampleRow(u, 1.0);  // row 1: horizon
          vec3 cBottom  = sampleRow(u, 2.0);  // row 2: BOTTOM of the sky

          // map dir.y (-1..+1) → height 0..1
          float h = clamp(dir.y * 0.5 + 0.5, 0.0, 1.0);

          // two-band gradient with a soft horizon
          vec3 ratios = vec3(0.5, 0.7, 1.0); // x=bottomStart, y=horizonCenter, z=topStart  (all in [0,1])
          float band = smoothstep(ratios.y - 0.10, ratios.y + 0.10, h);
          vec3  low  = mix(cBottom,  cHorizon, smoothstep(ratios.x, ratios.y, h));
          vec3  high = mix(cHorizon, cTop,     smoothstep(ratios.y, ratios.z, h));
          vec3  sky  = mix(low, high, band);

          // output (LUT authored in sRGB, but three.js will convert when colorSpace is sRGB on the texture;
          // we keep shader in linear; that’s fine since uSkyLUT was sampled through the fixed-function sampler)

          csm_DiffuseColor = vec4(sky, 1.0);
        }
      `,
  })
  const geometry = new THREE.BoxGeometry(50, 50, 50)
  const mesh = new THREE.Mesh(geometry, material)

  const fogColor = new THREE.Color(1, 1, 1)
  const fog = new THREE.Fog(fogColor, 0, 10000)

  async function load(url) {
    // '/skyLUT.png'
    lut = await textureLoader.loadAsync(url)
    lut.flipY = false
    lut.magFilter = THREE.LinearFilter
    lut.minFilter = THREE.LinearFilter
    lut.generateMipmaps = false
    lut.wrapS = THREE.RepeatWrapping // repeat horizontally
    lut.wrapT = THREE.ClampToEdgeWrapping // not vertically
    lut.anisotropy = 1
    lut.colorSpace = THREE.SRGBColorSpace
    lut.needsUpdate = true
    uniforms.uSkyLUT.value = lut
    uniforms.uSkyLUTSize.value.set(lut.image.width, lut.image.height)
    sampler.setImage(lut.image)
  }

  let _viewDistance = 8
  let _deviceViewDistance = 8
  function updateViewDistance() {
    const value = Math.min(_viewDistance, _deviceViewDistance)
    fog.near = CHUNK_WIDTH * (value - 2)
    fog.far = CHUNK_WIDTH * (value - 1)
  }

  let _elapsed = 0
  let _time = 0
  let _timeFrozen = false
  let _timeDayMinutes = 20

  function update(delta) {
    _elapsed += delta
    uniforms.uElapsed.value = _elapsed
    // increment time if not frozen
    if (!_timeFrozen) {
      _time += delta / (_timeDayMinutes * 60)
      uniforms.uTime.value = _time
      // update fog color to match horizon (3rd row of pixels)
      const target = sampler.sample(2, _time)
      fog.color.copy(target)
    }
  }

  return {
    load,
    mesh,
    fog,
    lut,
    uniforms,
    sample(row, t01) {
      return sampler.sample(row, t01)
    },
    update,
    get time() {
      return _time
    },
    set time(value) {
      _time = value
      uniforms.uTime.value = _time
    },
    set timeFrozen(value) {
      _timeFrozen = value
    },
    set timeDayMinutes(value) {
      _timeDayMinutes = value
    },
    set viewDistance(value) {
      _viewDistance = value
      updateViewDistance()
    },
    set deviceViewDistance(value) {
      _deviceViewDistance = value
      updateViewDistance()
    },
  }
}

function createSampler() {
  let width = 0
  let height = 0
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  let linear
  const col = new THREE.Color()

  function srgbToLinear(u) {
    const c = u / 255
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  }

  function setImage(image) {
    width = image.width
    height = image.height
    canvas.width = width
    canvas.height = height
    ctx.drawImage(image, 0, 0)
    const data = ctx.getImageData(0, 0, width, height).data
    linear = Array.from({ length: height }, () => Array(width))
    for (let row = 0; row < height; row++) {
      for (let col = 0; col < width; col++) {
        const i = (row * width + col) * 4
        const r = srgbToLinear(data[i + 0])
        const g = srgbToLinear(data[i + 1])
        const b = srgbToLinear(data[i + 2])
        linear[row][col] = new THREE.Color(r, g, b) // linear
      }
    }
  }

  // smooth horizontal sample of a LUT row at fractional column
  function sample(row, t01) {
    if (!linear) {
      col.set(0, 0, 0)
      return col
    }

    const x = (t01 - Math.floor(t01)) * width // [0,width)
    const i0 = Math.floor(x) % width
    const i1 = (i0 + 1) % width
    const f = x - i0

    const c0 = linear[row][i0]
    const c1 = linear[row][i1]

    // col = mix(c0, c1, f) in linear space
    col.copy(c0).lerp(c1, f)
    return col
  }

  return {
    setImage,
    sample,
  }
}
